import { useState, useCallback } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { operational } from "@mideye/design-tokens";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizeIpSearch } from "@/utils/ipSearch.ts";

const ipSearchTokens = operational.dashboard.ipSearch;

const textFieldSx = {
  width: { xs: 176, sm: 200 },
  "& .MuiOutlinedInput-root": {
    backgroundColor: ipSearchTokens.bg,
    fontSize: "0.875rem",
    "& fieldset": { borderColor: ipSearchTokens.border },
    "&:hover fieldset": { borderColor: ipSearchTokens.borderHover },
  },
};

/**
 * Top-nav IP search. On submit, navigates to /ip-activity with the IP applied
 * as a filter (backend decides exact vs prefix). Existing filters on
 * /ip-activity are preserved; navigating from other pages starts fresh.
 * Invalid input blocks submit and flags the field.
 */
export default function IpSearchBox() {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  // Keep the input in sync with the URL's ip_search param so that clearing the
  // filter chip (which removes the param) also empties the input. Uses the
  // setState-during-render pattern to avoid cascading renders from an effect.
  const urlIpSearch = location.pathname === "/ip-activity"
    ? new URLSearchParams(location.search).get("ip_search") ?? ""
    : "";
  const [lastSyncedIp, setLastSyncedIp] = useState(urlIpSearch);
  if (urlIpSearch !== lastSyncedIp) {
    setLastSyncedIp(urlIpSearch);
    setValue(urlIpSearch);
    setError(false);
  }

  const submit = useCallback(() => {
    const normalized = normalizeIpSearch(value);
    if (normalized === null) {
      setError(true);
      return;
    }
    setError(false);
    // Preserve existing IP Activity filters when already on /ip-activity so the
    // search box feels additive rather than resetting the page.
    const onIpActivity = location.pathname === "/ip-activity";
    const params = new URLSearchParams(onIpActivity ? location.search : "");
    params.set("ip_search", normalized);
    navigate(`/ip-activity?${params.toString()}`);
  }, [value, navigate, location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (error) setError(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <TextField
      size="small"
      placeholder="Search IP"
      value={value}
      error={error}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      sx={textFieldSx}
      slotProps={{
        htmlInput: { "aria-label": "Search IP address", "data-testid": "ip-search-input", maxLength: 15 },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={submit} edge="end" aria-label="Submit IP search" data-testid="ip-search-submit">
                <SearchIcon sx={{ fontSize: 18, color: ipSearchTokens.icon }} />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
